import React, { useState, useCallback, useEffect, useMemo } from 'react';
import Plus from 'lucide-react/dist/esm/icons/plus';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import ChevronUp from 'lucide-react/dist/esm/icons/chevron-up';
import Upload from 'lucide-react/dist/esm/icons/upload';
import X from 'lucide-react/dist/esm/icons/x';
import Send from 'lucide-react/dist/esm/icons/send';
import Save from 'lucide-react/dist/esm/icons/save';
import Download from 'lucide-react/dist/esm/icons/download';
import Link from 'lucide-react/dist/esm/icons/link';
import Copy from 'lucide-react/dist/esm/icons/copy';
import { useSurveyStore } from '../../stores/surveyStore';
import { useUIStore } from '../../stores/uiStore';
import { getTranslation } from '../../i18n';
import { surveyApi } from '../../services/api/survey';
import {
  Button, TextField, TextArea, FileUpload, DatePicker, Text, Badge, Switch, IconButton, useToast,
} from '../../components/ui';

const SurveyEditor: React.FC = () => {
  const { activeSurvey, createSurvey, updateSurvey, sendSurvey, setView, loadSurveys } = useSurveyStore();
  const { language } = useUIStore();
  const t = (key: string) => getTranslation(language, key);
  const { success, error: showError } = useToast();

  const [title, setTitle] = useState(activeSurvey?.title || '');
  const [questions, setQuestions] = useState<{ text: string }[]>(
    activeSurvey?.questions?.length
      ? activeSurvey.questions.map((q) => ({ text: q.question_text }))
      : [{ text: '' }],
  );
  const [emails, setEmails] = useState<string[]>(
    activeSurvey?.recipients?.map((r) => r.email) || [],
  );
  const [emailInput, setEmailInput] = useState('');
  const [deadline, setDeadline] = useState(
    activeSurvey?.deadline ? activeSurvey.deadline.split('T')[0] : '',
  );
  const [emailsExpanded, setEmailsExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [publicLinkEnabled, setPublicLinkEnabled] = useState(activeSurvey?.public_link_enabled || false);
  const [publicLinkToken, setPublicLinkToken] = useState(activeSurvey?.public_link_token || null);
  const [togglingLink, setTogglingLink] = useState(false);

  const isExisting = !!activeSurvey;
  const isDraft = !activeSurvey || activeSurvey.status === 'draft';

  const recipientNameByEmail = useMemo(() => {
    const map = new Map<string, string>();
    if (activeSurvey?.recipients) {
      for (const r of activeSurvey.recipients) {
        if (r.name) map.set(r.email.toLowerCase(), r.name);
      }
    }
    return map;
  }, [activeSurvey?.recipients]);

  const handleDownloadCsv = useCallback(() => {
    const rows = [['name', 'email']];
    for (const email of emails) {
      rows.push([recipientNameByEmail.get(email.toLowerCase()) || '', email]);
    }
    const csv = rows.map(r => r.map(c => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `recipients_${(activeSurvey?.title || 'survey').replace(/\s+/g, '_').slice(0, 30)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [emails, recipientNameByEmail, activeSurvey?.title]);

  useEffect(() => {
    if (activeSurvey) {
      setTitle(activeSurvey.title || '');
      setQuestions(
        activeSurvey.questions.length
          ? activeSurvey.questions.map((q) => ({ text: q.question_text }))
          : [{ text: '' }],
      );
      setEmails(activeSurvey.recipients.map((r) => r.email));
      setDeadline(activeSurvey.deadline ? activeSurvey.deadline.split('T')[0] : '');
      setPublicLinkEnabled(activeSurvey.public_link_enabled);
      setPublicLinkToken(activeSurvey.public_link_token);
    }
  }, [activeSurvey]);

  const addQuestion = useCallback(() => {
    setQuestions((prev) => [...prev, { text: '' }]);
  }, []);

  const removeQuestion = useCallback((idx: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const updateQuestion = useCallback((idx: number, text: string) => {
    setQuestions((prev) => prev.map((q, i) => (i === idx ? { text } : q)));
  }, []);

  const addEmail = useCallback(() => {
    const trimmed = emailInput.trim().toLowerCase();
    if (trimmed && trimmed.includes('@') && !emails.includes(trimmed)) {
      setEmails((prev) => [...prev, trimmed]);
      setEmailInput('');
    }
  }, [emailInput, emails]);

  const removeEmail = useCallback((email: string) => {
    setEmails((prev) => prev.filter((e) => e !== email));
  }, []);

  const handleEmailKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail();
    }
  }, [addEmail]);

  const handleFileUpload = useCallback(async (files: File[]) => {
    if (!activeSurvey || files.length === 0) return;
    setUploadingFile(true);
    try {
      const { data } = await surveyApi.uploadRecipients(activeSurvey.id, files[0]);
      const newEmails = data.emails.filter((e: string) => !emails.includes(e.toLowerCase()));
      if (newEmails.length > 0) {
        setEmails((prev) => [...prev, ...newEmails.map((e: string) => e.toLowerCase())]);
        success(t('surveyEditorToastExtractedEmails').replace('{{count}}', String(newEmails.length)).replace('{{filename}}', data.source_filename));
      } else {
        success(t('surveyEditorToastNoNewEmails'));
      }
    } catch {
      showError(t('surveyEditorErrorExtractEmails'));
    } finally {
      setUploadingFile(false);
    }
  }, [activeSurvey, emails, success, showError]);

  const handleTogglePublicLink = useCallback(async () => {
    if (isExisting && activeSurvey) {
      setTogglingLink(true);
      try {
        const { data } = await surveyApi.togglePublicLink(activeSurvey.id);
        setPublicLinkEnabled(data.public_link_enabled);
        setPublicLinkToken(data.public_link_token);
      } catch {
        showError(t('surveyEditorErrorToggleLink'));
      } finally {
        setTogglingLink(false);
      }
    } else {
      setPublicLinkEnabled((v) => !v);
    }
  }, [isExisting, activeSurvey, showError]);

  const publicLinkUrl = publicLinkToken
    ? `${window.location.origin}/survey/p/${publicLinkToken}`
    : null;

  const handleCopyLink = useCallback(() => {
    if (publicLinkUrl) {
      navigator.clipboard.writeText(publicLinkUrl);
      success(t('surveyEditorToastLinkCopied'));
    }
  }, [publicLinkUrl, success]);

  const buildPayload = useCallback(() => {
    const validQuestions = questions
      .filter((q) => q.text.trim())
      .map((q, i) => ({ question_text: q.text.trim(), position: i }));
    return {
      title: title.trim(),
      questions: validQuestions,
      recipient_emails: emails,
      deadline: deadline ? new Date(deadline + 'T23:59:59Z').toISOString() : null,
      public_link_enabled: publicLinkEnabled,
    };
  }, [title, questions, emails, deadline, publicLinkEnabled]);

  const handleSave = useCallback(async () => {
    const payload = buildPayload();
    if (!payload.title || payload.questions.length === 0) {
      showError(t('surveyEditorErrorTitleRequired'));
      return;
    }
    setSaving(true);
    try {
      if (isExisting) {
        await updateSurvey(activeSurvey!.id, payload);
      } else {
        await createSurvey(payload);
      }
      success(t('surveyEditorToastSaved'));
    } catch {
      showError(t('surveyEditorErrorSaveFailed'));
    } finally {
      setSaving(false);
    }
  }, [buildPayload, isExisting, activeSurvey, createSurvey, updateSurvey, success, showError]);

  const handleSaveAndSend = useCallback(async () => {
    const payload = buildPayload();
    if (!payload.title || payload.questions.length === 0) {
      showError(t('surveyEditorErrorTitleRequired'));
      return;
    }
    if (emails.length === 0 && !publicLinkEnabled) {
      showError(t('surveyEditorErrorRecipientRequired'));
      return;
    }
    setSending(true);
    try {
      let surveyId = activeSurvey?.id;
      if (isExisting) {
        await updateSurvey(activeSurvey!.id, payload);
      } else {
        const created = await createSurvey(payload);
        surveyId = created.id;
      }
      await sendSurvey(surveyId!);
      success(t('surveyEditorToastSent'));
      loadSurveys();
      setView('board');
    } catch {
      showError(t('surveyEditorErrorSendFailed'));
    } finally {
      setSending(false);
    }
  }, [buildPayload, emails, isExisting, activeSurvey, createSurvey, updateSurvey, sendSurvey, loadSurveys, setView, success, showError]);

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        {/* Title */}
        <div>
          <TextField
            label={t('surveyEditorLabelTitle')}
            value={title}
            onChange={(e) => setTitle(typeof e === 'string' ? e : e.target.value)}
            placeholder={t('surveyEditorPlaceholderTitle')}
            disabled={!isDraft}
          />
        </div>

        {/* Recipients */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Text variant="label">{t('surveyEditorLabelRecipients')}</Text>
            <div className="flex items-center gap-2">
              {emails.length > 0 && (
                <Badge variant="info" size="sm">
                  {emails.length !== 1
                    ? t('surveyEditorRecipientCountPlural').replace('{{count}}', String(emails.length))
                    : t('surveyEditorRecipientCount').replace('{{count}}', String(emails.length))}
                </Badge>
              )}
              {emails.length > 0 && (
                <IconButton
                  icon={<Download size={14} />}
                  variant="ghost"
                  aria-label="Download recipients CSV"
                  onClick={handleDownloadCsv}
                />
              )}
              {emails.length > 0 && (
                <button
                  onClick={() => setEmailsExpanded((v) => !v)}
                  className="text-content-muted hover:text-content transition-colors"
                >
                  {emailsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              )}
            </div>
          </div>

          {isDraft && (
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <TextField
                  value={emailInput}
                  onChange={(e) => setEmailInput(typeof e === 'string' ? e : e.target.value)}
                  placeholder={t('surveyEditorPlaceholderEmail')}
                  onKeyDown={handleEmailKeyDown}
                />
              </div>
              <Button variant="secondary" size="sm" onClick={addEmail}>{t('surveyEditorBtnAdd')}</Button>
            </div>
          )}

          {emailsExpanded && emails.length > 0 && (
            <div className="flex flex-wrap gap-2 p-3 rounded-xl bg-surface-primary border border-subtle max-h-48 overflow-y-auto">
              {emails.map((email) => {
                const name = recipientNameByEmail.get(email.toLowerCase());
                return (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-surface-secondary text-xs text-content-secondary"
                  >
                    {name && <span className="font-medium text-content">{name}</span>}
                    {email}
                    {isDraft && (
                      <button onClick={() => removeEmail(email)} className="text-content-muted hover:text-status-error transition-colors">
                        <X size={12} />
                      </button>
                    )}
                  </span>
                );
              })}
            </div>
          )}

          {!emailsExpanded && emails.length > 0 && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-surface-primary border border-subtle text-xs text-content-muted">
              <span className="truncate">
                {emails.slice(0, 3).map((email) => {
                  const name = recipientNameByEmail.get(email.toLowerCase());
                  return name ? `${name} (${email})` : email;
                }).join(', ')}
              </span>
              {emails.length > 3 && <span className="flex-shrink-0">+{emails.length - 3} more</span>}
            </div>
          )}

          {isDraft && isExisting && (
            <FileUpload
              onFiles={handleFileUpload}
              accept=".csv,.xlsx,.xls,.pdf,.txt"
              multiple={false}
              disabled={uploadingFile}
            >
              <div className="flex items-center justify-center gap-2 p-4 border border-dashed border-subtle rounded-xl text-content-muted text-xs hover:border-default hover:text-content-secondary transition-all cursor-pointer">
                <Upload size={14} />
                {uploadingFile ? t('surveyEditorUploadExtracting') : t('surveyEditorUploadLabel')}
              </div>
            </FileUpload>
          )}
        </div>

        {/* Public Link */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link size={14} className="text-content-muted" />
              <Text variant="label">{t('surveyEditorLabelPublicLink')}</Text>
            </div>
            <Switch
              checked={publicLinkEnabled}
              onChange={handleTogglePublicLink}
              disabled={togglingLink || !isDraft}
            />
          </div>
          <Text variant="caption" className="text-content-muted">
            {t('surveyEditorPublicLinkCaption')}
            {!isExisting && publicLinkEnabled && t('surveyEditorPublicLinkPendingSave')}
          </Text>
          {publicLinkEnabled && publicLinkUrl && (
            <div className="flex items-center gap-2 p-2.5 rounded-xl bg-surface-primary border border-subtle">
              <span className="flex-1 text-xs text-content-secondary truncate font-mono">
                {publicLinkUrl}
              </span>
              <IconButton
                icon={<Copy size={14} />}
                variant="ghost"
                aria-label={t('surveyEditorAriaLabelCopyLink')}
                onClick={handleCopyLink}
              />
            </div>
          )}
        </div>

        {/* Deadline */}
        <div>
          <Text variant="label" className="mb-2 block">{t('surveyEditorLabelDeadline')}</Text>
          <DatePicker
            value={deadline}
            onChange={setDeadline}
            placeholder={t('surveyEditorPlaceholderDeadline')}
            minDate={new Date().toISOString().split('T')[0]}
          />
        </div>

        {/* Questions */}
        <div className="space-y-4">
          <Text variant="label">{t('surveyEditorLabelQuestions')}</Text>
          {questions.map((q, idx) => (
            <div key={idx} className="flex gap-3 items-start">
              <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg bg-surface-secondary text-content-muted text-xs font-medium mt-1">
                {idx + 1}
              </span>
              <div className="flex-1">
                <TextArea
                  value={q.text}
                  onChange={(e) => updateQuestion(idx, e.target.value)}
                  placeholder={t('surveyEditorPlaceholderQuestion')}
                  rows={2}
                  disabled={!isDraft}
                />
              </div>
              {isDraft && questions.length > 1 && (
                <button
                  onClick={() => removeQuestion(idx)}
                  className="flex-shrink-0 mt-1 p-1.5 rounded-lg text-content-muted hover:text-status-error hover:bg-status-error/10 transition-all"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}
          {isDraft && (
            <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={addQuestion}>
              {t('surveyEditorBtnAddQuestion')}
            </Button>
          )}
        </div>

        {/* Actions */}
        {isDraft && (
          <div className="flex items-center gap-3 pt-4 border-t border-subtle">
            <Button
              variant="secondary"
              size="md"
              leftIcon={<Save size={16} />}
              onClick={handleSave}
              loading={saving}
            >
              {t('surveyEditorBtnSave')}
            </Button>
            <Button
              variant="primary"
              size="md"
              leftIcon={<Send size={16} />}
              onClick={handleSaveAndSend}
              loading={sending}
              disabled={emails.length === 0 && !publicLinkEnabled}
            >
              {t('surveyEditorBtnSaveAndSend')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(SurveyEditor);
